import { atom } from "jotai";
import { DeductionDocumentUploadRepository } from "@/infrastructure/repositories/deduction";
import { UploadDeductionDocumentUseCase } from "@/core/usecases/deduction/UploadDeductionDocument.useCase";
import { DeductionUploadDocument, documentUploadAtom } from "./documentUpload.atoms";
import { Deduction } from "@/core/entities/deduction";
import { deductionAtom, deductionsAtom } from "@/store/deduction/deduction";

// Action atoms

/**
 * Adds a document to the documentUploadAtom
 *
 * @param get - The get function from Jotai
 * @param set - The set function from Jotai
 */
export const addDocumentUploadAtom = atom(null, async (get, set, documents: DeductionUploadDocument[]) => {
  set(documentUploadAtom, {
    ...get(documentUploadAtom),
    documents: [...get(documentUploadAtom).documents, ...documents],
  });
});

/**
 * Clears all documents from the documentUploadAtom
 *
 * @param get - The get function from Jotai
 * @param set - The set function from Jotai
 */
export const clearDocumentUploadAtom = atom(null, async (get, set) => {
  set(documentUploadAtom, {
    ...get(documentUploadAtom),
    documents: [],
  });
});

/**
 * Removes a document from the documentUploadAtom
 *
 * @param get - The get function from Jotai
 * @param set - The set function from Jotai
 * @param documentId - The id of the document to remove
 */
export const removeDocumentAtom = atom(null, async (get, set, documentId: number) => {
  set(documentUploadAtom, {
    ...get(documentUploadAtom),
    documents: get(documentUploadAtom).documents.filter(d => d.processId !== documentId),
  });
});

/**
 * Uploads all documents in the documentUploadAtom
 *
 * @param get - The get function from Jotai
 * @param set - The set function from Jotai
 */
export const uploadDocumentAtom = atom(null, async (get, set) => {
  const documents = get(documentUploadAtom).documents;
  if (documents.length === 0) {
    return;
  }
  const deductionDocumentUploadRepository = new DeductionDocumentUploadRepository();
  const uploadDocumentUseCase = new UploadDeductionDocumentUseCase(deductionDocumentUploadRepository);

  const reversedDocuments = [...documents].reverse();

  for (const document of reversedDocuments) {
    //Check again if the document is present in the documentUploadAtom
    const documentCheck = get(documentUploadAtom).documents.find(d => d.processId === document.processId);
    if (!documentCheck) {
      continue;
    }

    if (document.isPending === false) {
      continue;
    }

    set(documentUploadAtom, {
      ...get(documentUploadAtom),
      documents: get(documentUploadAtom).documents.map(d => (d.processId === document.processId ? { ...d, isUploading: true } : { ...d, isUploading: false })),
    });
    try {
      const responseDocument = await uploadDocumentUseCase.execute(document.file);
      set(documentUploadAtom, {
        ...get(documentUploadAtom),
        documents: get(documentUploadAtom).documents.map(d =>
          d.processId === document.processId ? { ...d, response: responseDocument, isUploading: false, isUploaded: true, isPending: false } : d
        ),
      });
    } catch {
      set(documentUploadAtom, {
        ...get(documentUploadAtom),
        documents: get(documentUploadAtom).documents.map(d =>
          d.processId === document.processId ? { ...d, isUploading: false, isPending: false, error: "Failed to upload document" } : d
        ),
      });
    }
  }
});

/**
 * Opens the document upload modal
 *
 * @param get - The get function from Jotai
 * @param set - The set function from Jotai
 */
export const openDocumentUploadAtom = atom(null, async (get, set, deductionId: number) => {
  set(documentUploadAtom, {
    ...get(documentUploadAtom),
    isDocumentUploadOpen: true,
    selectedModalId: deductionId,
  });
});

/**
 * Closes the document upload modal
 *
 * @param get - The get function from Jotai
 * @param set - The set function from Jotai
 */
export const closeDocumentUploadAtom = atom(null, async (get, set) => {
  set(documentUploadAtom, {
    ...get(documentUploadAtom),
    isDocumentUploadOpen: false,
    selectedModalId: null,
  });
});

/**
 * Sets the isAttaching state
 *
 * @param get - The get function from Jotai
 * @param set - The set function from Jotai
 */
export const setIsAttachingAtom = atom(null, async (get, set, isAttaching: boolean) => {
  set(documentUploadAtom, {
    ...get(documentUploadAtom),
    isAttaching,
  });
});

/**
 * Update deduction after document upload
 */
export const updateDeductionAtom = atom(null, async (get, set, deduction: Deduction) => {
  const deductions = get(deductionsAtom);
  set(deductionAtom, {
    ...get(deductionAtom),
    deductions: deductions.map(_deduction => (_deduction.id === deduction.id ? deduction : _deduction)),
  });
});
